import { uploadFileApi } from "@/api/services/global/file";
import { IconAttachFile } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { COMMON_DROPZONE_ACCEPT, COMMON_FILE_TYPES_EXTENSION, COMMON_MINE_TYPES, FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { MessageFileItem } from "@/features/chat/components/MessageFileItem";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { background, button, divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertMbToByte, getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";
import { Box, CircularProgress, Stack, styled, Typography } from "@mui/material";
import { KeyboardEventHandler, useEffect, useMemo, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

export const InputMsg = () => {
  const { currentRoom } = useChat();
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const rules: TFileRules = {
    acceptMimeTypes: COMMON_MINE_TYPES,
    acceptFileTypesExtension: COMMON_FILE_TYPES_EXTENSION,
    dropzoneAccept: COMMON_DROPZONE_ACCEPT,
  };

  const isRomDeleted = useMemo(() => !!currentRoom.roomData?.creator.deletedAt, [currentRoom]);

  const handleSendMessage = async () => {
    try {
      setLoading(true);
      const arrfilePath =
        uploadedFiles.length > 0
          ? await Promise.all(
              uploadedFiles.map(async (file) => {
                const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.CHAT_FILE });
                return rs.data.filePath;
              }),
            )
          : [];
      await currentRoom.sendMsg(inputValue, arrfilePath);
      setInputValue("");
      setUploadedFiles([]);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };
  const handleUploadFile = async (files: File[]) => {
    if (files.some((file) => file.size === 0)) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }
    if (
      !!rules.acceptMimeTypes &&
      files.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
    ) {
      toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []).join("、")}です。`);
      return;
    }

    const invalidPdf = await checkFileListWhetherIsActualPdf(files);
    if (invalidPdf) {
      toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
      return;
    }

    if (files.some((f) => f.size > convertMbToByte(10))) {
      toast.error(`ファイルサイズは10MB以内でお願いします。`);
      return;
    }

    if (files.length + uploadedFiles.length > 8) {
      toast.error(`ファイルは最大8件までアップロードできます。`);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      if (files.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }
      setUploadedFiles((prev) => [...prev, ...files]);
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (loading || e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      if (!inputValue) return;
      await handleSendMessage();
    }
  };

  const onFocus = async () => {
    await currentRoom?.readMsg();
  };

  const [showDropZone, setShowDropZone] = useState(false);

  useEffect(() => {
    const handleDragEnter = (event: DragEvent) => {
      event.preventDefault();
      setShowDropZone(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null && showDropZone) {
        setShowDropZone(false);
      }
    };

    const handleDragEnd = (event: DragEvent) => {
      event.preventDefault();
      setShowDropZone(false);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [showDropZone]);

  const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    setShowDropZone(false);
    try {
      if (
        !!rules.acceptMimeTypes &&
        fileRejections.some((file) => !rules.acceptMimeTypes?.includes(file.file.name.includes(".rar") ? "application/vnd.rar" : file.file.type))
      ) {
        toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []).join("、")}です。`);
        return;
      }

      if (
        !!rules.acceptMimeTypes &&
        acceptedFiles.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
      ) {
        toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []).join("、")}です。`);
        return;
      }

      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      if (acceptedFiles.some((f) => f.size > convertMbToByte(10))) {
        toast.error(`ファイルサイズは10MB以内でお願いします。`);
        return;
      }

      if (acceptedFiles.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }

      setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    } catch (error) {
      showError(error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: rules.dropzoneAccept,
    noClick: true,
  });

  return (
    <>
      <InputMsgWrapper>
        <Button component="label" disabled={isRomDeleted}>
          <IconAttachFile width={24} height={24} />
          <VisuallyHiddenInput
            type="file"
            accept={rules?.acceptFileTypesExtension?.join(",")}
            onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
            onClick={(event) => {
              event.currentTarget.value = "";
            }}
            multiple={true}
          />
        </Button>
        <Box flex={1}>
          {uploadedFiles.length > 0 && (
            <Stack
              position="absolute"
              width={620}
              zIndex={100}
              bottom={52}
              justifyContent="flex-start"
              alignItems="flex-start"
              mb={1}
              columnGap={4}
              rowGap={1}
              flexDirection="row"
              flexWrap="wrap"
              bgcolor={background.primary}
              padding={1}
              borderRadius={rounded.sm}
              border={`1px solid ${divider.middle}`}
            >
              {uploadedFiles.map((file, index) => {
                if (file.type && MIME_TYPES.IMAGE.some((item) => item.includes(file.type))) {
                  return (
                    <Avatar
                      key={index}
                      imageSrc={URL.createObjectURL(file)}
                      width={120}
                      height={120}
                      onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))}
                    />
                  );
                }
                return (
                  <MessageFileItem key={index} file={file} onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))} />
                );
              })}
            </Stack>
          )}
          <Input
            sx={{ boxShadow: "0px 4px 8px 0px #0000001A", borderRadius: rounded.xs }}
            multiline
            maxRows={4}
            autoComplete="off"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            fullWidth
            size="lg"
            disabled={loading || isRomDeleted}
          />
        </Box>

        <Button onClick={handleSendMessage} disabled={loading || isRomDeleted || (!inputValue && uploadedFiles.length === 0)}>
          {loading ? <CircularProgress size={20} color="inherit" /> : <Typography variant="sub16Semi">＞</Typography>}
        </Button>
      </InputMsgWrapper>

      {showDropZone && !isRomDeleted && (
        <Box
          {...getRootProps()}
          position="absolute"
          zIndex={5000}
          width="772px"
          height="calc(100% - 340px)"
          bottom={100}
          sx={{
            border: `1px dashed ${divider.middle}`,
            borderColor: isDragActive ? button.primary : undefined,
            backgroundColor: background.white,
            opacity: 0.9,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body14">こちらにドラッグ＆ドロップしてください</Typography>
        </Box>
      )}
    </>
  );
};

const InputMsgWrapper = styled(Box)`
  position: relative;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  .MuiInputBase-root {
    padding: 0;
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
